import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../context/ThemeContext';
import { getAntdThemeConfig } from '../../../utils/antdTheme';
import { AppDispatch, RootState } from '../../../redux/store';
import {
  fetchAdminAnalytics,
  setTimeRange,
  setShowSessionsDetails,
  toggleSessionsDetails,
} from './store/adminAnalyticsSlice';
import { PageWrapper } from './Styles';
import OverviewSection from './OverviewSection';
import AnalysisSections from './AnalysisSections';

const AdminAnalytics: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const {
    overview,
    activeSessions,
    merchantsWithSessions,
    timeRange,
    showSessionsDetails,
  } = useSelector((state: RootState) => state.adminAnalytics);

  useEffect(() => {
    dispatch(fetchAdminAnalytics());
  }, [dispatch]);

  const themeConfig = useMemo(
    () => ({ mode: (isDarkMode ? 'dark' : 'light') as 'light' | 'dark' }),
    [isDarkMode]
  );
  const antdConfig = useMemo(() => getAntdThemeConfig(isDarkMode), [isDarkMode]);

  const handleTimeRangeChange = (value: Parameters<typeof setTimeRange>[0]) => {
    dispatch(setTimeRange(value));
    dispatch(fetchAdminAnalytics(value));
  };
  const handleCloseSessions = () => dispatch(setShowSessionsDetails(false));
  const handleToggleSessions = () => dispatch(toggleSessionsDetails());

  return (
    <ThemeProvider theme={themeConfig}>
      <ConfigProvider theme={antdConfig}>
        <PageWrapper>
          <OverviewSection
            overview={overview}
            activeSessions={activeSessions}
            merchantsWithSessions={merchantsWithSessions}
            timeRange={timeRange}
            showSessionsDetails={showSessionsDetails}
            onTimeRangeChange={handleTimeRangeChange}
            onToggleSessions={handleToggleSessions}
            onCloseSessions={handleCloseSessions}
          />
          <AnalysisSections overview={overview} activeSessions={activeSessions} />
        </PageWrapper>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default AdminAnalytics;
